import { createReactExtension } from '@backstage/core-plugin-api';
import {
  createFrontendPlugin,
  PageBlueprint,
  PluginHeaderActionBlueprint,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import { homePlugin } from '@backstage/plugin-home';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { RiCalendarEventLine } from '@remixicon/react';
import {
  overviewRouteRef,
  recordingsRouteRef,
  rootRouteRef,
  statisticsRouteRef,
} from './routes';

/**
 * A page to display the techSpot event calendar and link to create new events.
 *
 * @public
 */
export const techSpotEventCalendarPage = PageBlueprint.make({
  // name: 'techspot-event-calendar', // NFS: if this is present the nav-item can be disabled but the page itself is not working anymore
  params: {
    path: '/techspot-event-calendar',
    routeRef: rootRouteRef,
    title: 'TechSpot Event Calendar',
    icon: <RiCalendarEventLine />,
  },
});

// Sub-pages — each becomes a tab in the PluginHeader
const overviewSubPage = SubPageBlueprint.make({
  name: 'overview',
  params: {
    path: 'overview',
    title: 'Overview',
    routeRef: overviewRouteRef,
    loader: () =>
      import('./components/techspot-event-calendar-page/overview').then(m => (
        <m.TechSpotEventCalendarOverviewPage />
      )),
  },
});

const recordingsSubPage = SubPageBlueprint.make({
  name: 'recordings',
  params: {
    path: 'recordings',
    title: 'Recordings from past events',
    routeRef: recordingsRouteRef,
    loader: () =>
      import('./components/techspot-event-calendar-page/recordings').then(m => (
        <m.TechSpotEventCalendarRecordingsPage />
      )),
  },
});

const statisticsSubPage = SubPageBlueprint.make({
  name: 'statistics',
  params: {
    path: 'statistics',
    title: 'Statistics',
    routeRef: statisticsRouteRef,
    loader: () =>
      import('./components/techspot-event-calendar-page/statistics').then(m => (
        <m.TechSpotEventCalendarStatisticsPage />
      )),
  },
});

// @NFS: Exporting the widget as a HomePageWidgetBlueprint to be used in the app.tsx overrides
export const techSpotEventCalendarWidget: ExtensionDefinition =
  HomePageWidgetBlueprint.make({
    name: 'techspot-event-calendar-widget',
    params: {
      title: 'TechSpot Event Calendar',
      description: 'Displays the last x TechSpot events.',
      settings: {
        schema: {
          title: 'Calendar settings',
          type: 'object',
          properties: {
            customAmountOfDays: {
              title: 'Amount of days',
              type: 'number',
              default: 7,
            },
          },
        },
      },
      components: async () =>
        import('./components/techspot-event-calendar-widget').then(m => ({
          Content: m.TechSpotEventCalendarWidget,
        })),
    },
  });

export const headerActions = PluginHeaderActionBlueprint.make({
  params: defineParams =>
    defineParams({
      loader: () =>
        import('./components/header-actions').then(m => <m.HeaderActions />),
    }),
});

export const techSpotEventCalendarPlugin = createFrontendPlugin({
  pluginId: 'techspot-event-calendar',
  extensions: [
    techSpotEventCalendarPage,
    overviewSubPage,
    recordingsSubPage,
    statisticsSubPage,
    headerActions,
    // @NFS: when having deprecated the "old" widget we can use the NFS version - needs some adjustments to not cause double Cards to show
    // techSpotEventCalendarWidget,
  ],
  routes: {
    root: rootRouteRef,
  },
});

/**
 * A widget to display the last x techSpot events.
 * @NFS: this widget needs to use the WidgetBlueprint when the new HomePage plugin is in place
 *
 * @public
 */
export const TechSpotEventCalendarWidget = homePlugin.provide(
  createReactExtension({
    name: 'TechSpotEventCalendarWidget',
    component: {
      lazy: () =>
        import('./components/techspot-event-calendar-widget').then(
          m => m.TechSpotEventCalendarWidget,
        ),
    },
    // See: https://github.com/backstage/backstage/tree/master/plugins/home#creating-customizable-components
    data: {
      'home.widget.config': {
        settings: {
          schema: {
            title: 'Calendar settings',
            type: 'object',
            properties: {
              customAmountOfDays: {
                title: 'Amount of days',
                type: 'number',
                default: 7,
              },
            },
          },
        },
      },
    },
  }),
);
